import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('voip-vs-mobile-numbers');

export default function VoipVsMobilePage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Line-type lookup reports whether a number is mobile, landline or VoIP by checking which carrier currently holds it. VoIP correlates with fraud because throwaway numbers are cheap and instant — but it does not prove it, because tens of millions of ordinary people and most businesses now use VoIP as their real number.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="What a lookup can actually tell you">
        <p>
          A number is queried against carrier data to find who holds it now. Two facts shape how
          much weight the answer deserves.
        </p>
        <LearnTable
          head={['Field', 'Means', 'Confidence']}
          rows={[
            ['Valid / invalid', 'Conforms to the E.164 plan and the range is allocated', <Lead key="a">High</Lead>],
            ['Country', 'Derived from the country code', <Lead key="b">High</Lead>],
            ['Line type', 'mobile, landline, VoIP, toll-free, or unknown', 'Good, with the caveats below'],
            ['Carrier', 'The operator currently holding the number', 'Good where portability data is current'],
            ['Reachable', <Lead key="c">Not answered by a lookup at all</Lead>, '—'],
          ]}
        />
        <p>
          The last row is the one to internalise. A lookup tells you a number is well-formed and who
          holds it. It cannot tell you a handset is switched on, or that the person filling your
          form is holding it. Only sending a code establishes that, and even then it establishes
          possession, not identity.
        </p>
      </LearnSection>

      <LearnSection title="Number portability breaks the naive reading">
        <p>
          Line type is inferred from the operator holding a number, and numbers move between
          operators. Portability is why the field is good rather than authoritative:
        </p>
        <LearnList>
          <li>
            A number issued as a landline and ported to a mobile network reads mobile — correctly,
            but it looks nothing like its allocated range.
          </li>
          <li>
            A mobile number ported to a VoIP provider reads VoIP even though the same person still
            answers it on the same phone.
          </li>
          <li>
            Portability data propagates at different speeds by country, so a recently ported number
            can report its previous type for a while.
          </li>
        </LearnList>
        <p>
          Practical consequence: treat line type as a probabilistic signal with a real error rate,
          not as a property of the number.
        </p>
      </LearnSection>

      <LearnSection title="Why VoIP correlates with fraud, and why that is not enough">
        <p>
          The correlation is real and the mechanism is obvious. A VoIP number can be provisioned in
          seconds, costs almost nothing, needs no identity check in most jurisdictions, and can be
          discarded and replaced. Anyone farming signups uses VoIP because everything else is
          slower and more expensive.
        </p>
        <p>Then there is the other population, which is much larger:</p>
        <LearnTable
          head={['Who', 'Why their number is VoIP']}
          rows={[
            ['Almost every business phone system', 'Cloud PBX replaced on-premise telephony years ago'],
            ['Remote workers', 'A softphone is the work number'],
            ['Google Voice and equivalents', 'A stable personal number people have held for a decade'],
            ['Anyone who kept a number after moving', 'Ported to VoIP to retain it across countries'],
            ['Privacy-conscious users', 'A deliberate second number, exactly like a forwarding email alias'],
          ]}
        />
        <p>
          So VoIP is evidence, not proof — and blocking on it alone refuses a large, wealthy,
          entirely legitimate group. It is structurally the same error as blocking{' '}
          <Link to="/learn/disposable-email-addresses" className="text-accent underline underline-offset-4">
            privacy forwarding aliases
          </Link>{' '}
          as disposable addresses: the signal is real, the population it catches is mostly innocent.
        </p>
      </LearnSection>

      <LearnSection title="Using the signal without the collateral damage">
        <LearnSubsection title="Score it, and let other signals move the total">
          <p>
            VoIP alone should not decide anything. VoIP <em>plus</em> a{' '}
            <Link to="/learn/disposable-email-addresses" className="text-accent underline underline-offset-4">
              disposable email domain
            </Link>{' '}
            <em>plus</em> a signup at 04:00 <em>plus</em> a{' '}
            <Link to="/learn/multi-accounting" className="text-accent underline underline-offset-4">
              canonical mailbox
            </Link>{' '}
            already seen this week is a different proposition — and it is the combination doing the
            work, not any one field.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Escalate friction rather than refusing">
          <p>
            Send the verification code. A real customer completes it in twenty seconds; someone
            cycling through throwaway numbers is slowed to the cost of each new one. That converts
            a binary block into an economic one, which is the goal.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Gate the benefit, not the account">
          <p>
            As with the rest of this cluster: the thing worth protecting is the trial, the referral
            credit or the payout, not the existence of the account. Restricting the benefit costs
            nothing when the signal is wrong.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Reject invalid before analysing line type">
          <p>
            An unallocated range or a malformed number is a hard fact and free to act on. Do that
            first; it removes the noise before you start weighing anything probabilistic.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="One thing to check in your own data">
        <p>
          Before adopting any VoIP rule, measure it against customers you already have. Run line-type
          lookup across your existing paying base and see what share is VoIP.
        </p>
        <p>
          For most B2B products the answer is a large fraction, because business telephony is VoIP.
          A rule that would have blocked a meaningful share of your current customers is a rule that
          will block their replacements, and that is far easier to see before deployment than after.{' '}
          <Link to="/features/phone-validation" className="text-accent underline underline-offset-4">
            Bulk validation
          </Link>{' '}
          exists for exactly this kind of check.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is line-type lookup?',
    a: 'A check against carrier data that reports whether a number is mobile, landline, VoIP or toll-free, along with the operator currently holding it. It also confirms the number conforms to the E.164 numbering plan and that the range is allocated.',
  },
  {
    q: 'Should I block VoIP numbers at signup?',
    a: 'No. VoIP correlates with fraud because throwaway numbers are cheap and instant, but almost every business phone system is now VoIP, along with remote workers and anyone using Google Voice. Blocking on it alone refuses a large and entirely legitimate population.',
  },
  {
    q: 'Can a lookup tell me whether a number is reachable?',
    a: 'No. It tells you the number is well-formed and who holds it. Whether a handset is switched on, and whether the person filling your form is holding it, are different questions that only sending a verification code answers — and that proves possession, not identity.',
  },
  {
    q: 'Why does a mobile number sometimes report as VoIP?',
    a: 'Number portability. Line type is inferred from the operator currently holding the number, so a mobile ported to a VoIP provider reads VoIP even though the same person answers on the same phone. Portability data also propagates at different speeds by country.',
  },
  {
    q: 'What should I do with a VoIP number instead of blocking it?',
    a: 'Treat it as one input to a risk score and escalate friction rather than refusing. Send the verification code: a real customer completes it in seconds, while someone cycling through throwaway numbers pays for each new one. That turns a binary block into an economic one.',
  },
  {
    q: 'How do I know if a VoIP rule would hurt me?',
    a: 'Run line-type lookup across your existing paying customers before deploying anything. For most B2B products a large share will be VoIP, because business telephony is VoIP. A rule that would have blocked current customers will block their replacements.',
  },
];
